/**
 * Sandboxa HTML vindo do WordPress (v-html) pra que estilos injetados no
 * conteúdo NÃO vazem pra página inteira (header, menu bar, etc).
 *
 * Abordagem: extrai cada bloco <style> do HTML, prefixa todo seletor com
 * a classe de sandbox, e neutraliza seletores globais (html, body, *, :root)
 * pra que virem o próprio sandbox.
 *
 * Não usa Shadow DOM — preserva SSR, SEO, e scripts do post continuam
 * rodando normalmente no document.
 */

const GLOBAL_SELECTORS = new Set(['html', 'body', '*', ':root', ':host']);

/**
 * Isola o "primeiro composto" do seletor (antes do primeiro combinator
 * descendente/filho/irmão), respeitando parênteses.
 * Ex: ".foo.bar > .baz" → ["]foo.bar", " > .baz"]
 */
function splitFirstCompound(selector: string): [string, string] {
  const len = selector.length;
  let i = 0;
  let depth = 0;

  while (i < len) {
    const ch = selector[i]!;

    if (ch === '(' || ch === '[') depth++;
    else if (ch === ')' || ch === ']') depth--;
    else if (depth === 0 && /[\s>+~]/.test(ch)) {
      return [selector.slice(0, i), selector.slice(i)];
    }
    i++;
  }

  return [selector, ''];
}

/**
 * Prefixa um único seletor composto com a classe de sandbox.
 * Emite DUAS variantes pra cobrir:
 *   - Elemento dentro do sandbox: ".sandbox .hero"
 *   - Próprio sandbox tendo a classe: ".sandbox.hero"
 * Isso resolve o caso onde o autor escreve ".hero" esperando que case
 * com o próprio container (que antes era o <body>, agora é o sandbox).
 *
 * Casos especiais:
 *   - "html" / "body" / "*" / ":root" → ".sandbox"
 *   - "body::before" → ".sandbox::before"
 *   - "body .foo" → ".sandbox .foo"
 *   - "@keyframes ..." → tratado fora (não prefixa)
 */
function prefixSelector(selector: string, scope: string): string {
  const trimmed = selector.trim();

  if (!trimmed) return trimmed;

  // Já começa com o escopo → não duplica
  if (trimmed.startsWith(scope)) return trimmed;

  // Global puro: html, body, *, :root → vira o próprio escopo
  if (GLOBAL_SELECTORS.has(trimmed)) return scope;

  // Global com pseudo-elemento: body::before → .sandbox::before
  for (const g of GLOBAL_SELECTORS) {
    if (trimmed.startsWith(g + '::') || trimmed.startsWith(g + ':')) {
      return scope + trimmed.slice(g.length);
    }
  }

  // Global combinado: "body .foo" → ".sandbox .foo"
  for (const g of GLOBAL_SELECTORS) {
    if (trimmed.startsWith(g + ' ')) {
      return scope + trimmed.slice(g.length);
    }
  }

  // Seletor normal: gera variante "descendente" + variante "self"
  // ".hero" → ".sandbox .hero, .sandbox.hero"
  // ".promotions-view .thumb img" → ".sandbox .promotions-view .thumb img, .sandbox.promotions-view .thumb img"
  // "h1" → ".sandbox h1" (tag selector não pode fundir com .sandbox)
  const [firstCompound, rest] = splitFirstCompound(trimmed);
  const descendant = `${scope} ${trimmed}`;

  // Só gera variante "self" quando o primeiro composto começa com
  // algo combinável com a classe de escopo: ".foo", "#foo", "[attr]", ":pseudo-class"
  const canFuse =
    firstCompound.startsWith('.') ||
    firstCompound.startsWith('#') ||
    firstCompound.startsWith('[') ||
    (firstCompound.startsWith(':') && !firstCompound.startsWith('::'));

  if (!canFuse) return descendant;

  const self = `${scope}${firstCompound}${rest}`;

  return `${descendant}, ${self}`;
}

/**
 * Divide uma lista de seletores por vírgula, respeitando vírgulas dentro de
 * parênteses (ex: :is(a, b)).
 */
function splitSelectorList(list: string): string[] {
  const result: string[] = [];
  let depth = 0;
  let current = '';
  let i = 0;
  const len = list.length;

  while (i < len) {
    const ch = list[i]!;

    // String: copia tudo até fechar
    if (ch === '"' || ch === "'") {
      const quote = ch;

      current += ch;
      i++;
      while (i < len) {
        current += list[i];
        if (list[i] === '\\' && i + 1 < len) {
          i++;
          current += list[i];
          i++;
          continue;
        }
        if (list[i] === quote) {
          i++;
          break;
        }
        i++;
      }
      continue;
    }

    // Comentário
    if (ch === '/' && list[i + 1] === '*') {
      current += '/*';
      i += 2;
      while (i < len && !(list[i] === '*' && list[i + 1] === '/')) {
        current += list[i];
        i++;
      }
      if (i < len) {
        current += '*/';
        i += 2;
      }
      continue;
    }

    if (ch === '(' || ch === '[') depth++;
    else if (ch === ')' || ch === ']') depth--;

    if (ch === ',' && depth === 0) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
    i++;
  }

  if (current.trim()) result.push(current);

  return result;
}

/**
 * Prefixa todos os seletores de uma string CSS dentro de um escopo.
 * Lida com at-rules aninhadas (@media, @supports) recursivamente.
 * Deixa @keyframes, @font-face, @charset, @import intocados.
 */
/**
 * Avança o índice `i` no CSS ignorando strings ('...' / "..."),
 * comentários (slash-star ... star-slash), e opcionalmente
 * parando em caracteres especificados.
 *
 * Retorna o novo índice.
 */
function skipNonCode(css: string, i: number): number {
  const len = css.length;

  if (i >= len) return i;

  const ch = css[i];

  // Comentário
  if (ch === '/' && css[i + 1] === '*') {
    i += 2;
    while (i < len && !(css[i] === '*' && css[i + 1] === '/')) i++;

    return Math.min(i + 2, len);
  }

  // String
  if (ch === '"' || ch === "'") {
    const quote = ch;

    i++;
    while (i < len) {
      if (css[i] === '\\') {
        i += 2;
        continue;
      }
      if (css[i] === quote) return i + 1;
      i++;
    }

    return i;
  }

  return i;
}

/**
 * Encontra o próximo `{`, `}`, ou `;` fora de strings/comentários.
 * Retorna a posição encontrada (ou `len` se não achar).
 */
function findNextDelimiter(
  css: string,
  start: number,
  delimiters: string,
): number {
  const len = css.length;
  let i = start;

  while (i < len) {
    const ch = css[i]!;

    if (ch === '/' && css[i + 1] === '*') {
      i = skipNonCode(css, i);
      continue;
    }
    if (ch === '"' || ch === "'") {
      i = skipNonCode(css, i);
      continue;
    }
    if (delimiters.includes(ch)) return i;
    i++;
  }

  return len;
}

/**
 * Encontra o `}` que casa com um `{` já consumido, respeitando
 * aninhamento, strings e comentários.
 *
 * @param css fonte CSS
 * @param start posição logo APÓS o `{` de abertura
 * @returns posição do `}` que fecha
 */
function findMatchingBrace(css: string, start: number): number {
  const len = css.length;
  let i = start;
  let depth = 1;

  while (i < len && depth > 0) {
    const ch = css[i]!;

    if (ch === '/' && css[i + 1] === '*') {
      i = skipNonCode(css, i);
      continue;
    }
    if (ch === '"' || ch === "'") {
      i = skipNonCode(css, i);
      continue;
    }
    if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0) return i;
    }
    i++;
  }

  return i;
}

function scopeCss(css: string, scope: string): string {
  let result = '';
  let i = 0;
  const len = css.length;

  while (i < len) {
    // Preserva whitespace
    while (i < len && /\s/.test(css[i]!)) {
      result += css[i];
      i++;
    }
    if (i >= len) break;

    // Preserva comentários
    if (css[i] === '/' && css[i + 1] === '*') {
      const end = skipNonCode(css, i);

      result += css.slice(i, end);
      i = end;
      continue;
    }

    // At-rule
    if (css[i] === '@') {
      const atStart = i;
      const delimPos = findNextDelimiter(css, i, '{;');

      const prelude = css.slice(atStart, delimPos);
      const name = prelude.match(/@([\w-]+)/)?.[1]?.toLowerCase() ?? '';

      if (delimPos >= len) {
        result += prelude;
        break;
      }

      if (css[delimPos] === ';') {
        result += prelude + ';';
        i = delimPos + 1;
        continue;
      }

      // css[delimPos] === '{'
      const blockStart = delimPos + 1;
      const blockEnd = findMatchingBrace(css, blockStart);
      const inner = css.slice(blockStart, blockEnd);

      const skipScoping = [
        'keyframes',
        '-webkit-keyframes',
        'font-face',
        'counter-style',
        'page',
      ].some((n) => name.endsWith(n));

      if (skipScoping) {
        result += prelude + '{' + inner + '}';
      } else {
        // @media, @supports → recursivo
        result += prelude + '{' + scopeCss(inner, scope) + '}';
      }
      i = blockEnd + 1;
      continue;
    }

    // Regra normal: acha "{" que abre o bloco
    const ruleStart = i;
    const delimPos = findNextDelimiter(css, i, '{}');

    if (delimPos >= len || css[delimPos] === '}') {
      result += css.slice(ruleStart, delimPos);
      if (css[delimPos] === '}') {
        result += '}';
        i = delimPos + 1;
      } else {
        i = delimPos;
      }
      continue;
    }

    const selectorList = css.slice(ruleStart, delimPos);
    const scopedSelectors = splitSelectorList(selectorList)
      .map((s) => prefixSelector(s, scope))
      .join(', ');

    const blockStart = delimPos + 1;
    const blockEnd = findMatchingBrace(css, blockStart);
    const declarations = css.slice(blockStart, blockEnd);

    result += scopedSelectors + '{' + declarations + '}';
    i = blockEnd + 1;
  }

  return result;
}

/**
 * Processa HTML do WP: encontra todos os <style>, escopa o CSS dentro deles
 * pra que só afete elementos dentro do container com a classe `scopeClass`.
 *
 * @param html   HTML cru vindo do WordPress
 * @param scopeClass classe CSS do container sandbox (com "." incluído, ex: ".blog-content-sandbox")
 */
export function scopeWpHtml(html: string, scopeClass: string): string {
  if (!html) return html;

  return html.replace(
    /<style\b([^>]*)>([\s\S]*?)<\/style>/gi,
    (_match, attrs, css) => {
      const scoped = scopeCss(css, scopeClass);

      return `<style${attrs}>${scoped}</style>`;
    },
  );
}
